import { useEffect, useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import {
  HookFormBaseDropdown,
  HookFormCheckbox,
  HookFormColorPicker,
} from "@/components_v2/FormFields/ReactHookForm/fields";
import FormFieldWrapper from "@/components_v2/Wrapper/FormFieldWrapper";
import {
  SurveyViewFormCustomizationProps,
  SurveyViewFormValues,
} from "../../types";
import Preview from "@/features/survey-views/form/components/Preview";
import BaseTooltip from "@/components_v2/Tooltip/BaseTooltip";
import {
  useLazyGetOrganizationByIdQuery,
  useGetSurveyContributorsQuery,
} from "@/redux/service";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import { selectCurrentUser } from "@/redux/authentication/selectors";

import "./styles.css";

const SurveyViewFormCustomization = ({
  form,
}: SurveyViewFormCustomizationProps) => {
  const { t } = useTranslation();

  const currentUser = useSelector(selectCurrentUser);
  const [getOrganizationById, organizationResponse] =
    useLazyGetOrganizationByIdQuery();
  const formData = useWatch({
    control: form.control,
  }) as SurveyViewFormValues;
  const { data: surveyContributors, isLoading: isSurveyContributorsLoading } =
    useGetSurveyContributorsQuery(formData.survey);

  useEffect(() => {
    if (currentUser && currentUser.organization) {
      getOrganizationById(currentUser.organization?.id);
    }
  }, [currentUser]);

  useEffect(() => {
    const currentValue = form.getValues("config.includeOrganizationSummary");
    if (!currentValue) {
      form.setValue("config.includeContributors", false);
    }

    const subscription = form.watch((value, { name }) => {
      if (
        name === "config.includeOrganizationSummary" &&
        !value.config?.includeOrganizationSummary
      ) {
        form.setValue("config.includeContributors", false);
      }
    });

    return () => subscription.unsubscribe();
  }, [form]);

  const isOrganizationOverviewBlank = useMemo(() => {
    if (organizationResponse.data) {
      return organizationResponse.data.aboutUs.length === 0;
    }
    return false;
  }, [organizationResponse]);

  return (
    <div className="survey-view-content">
      <h2 className="header-m-medium">
        {t("surveyViews.labels.customizePdfDescription")}
      </h2>

      <div className="survey-view-customization-form">
        <div className="survey-view-customization-form-panel">
          <div className="survey-view-customization-form-panel-general">
            <h3 className="body-m-medium">{t("surveyViews.labels.general")}</h3>

            <div className="survey-view-customization-form-panel-general-fields">
              <HookFormCheckbox
                name="config.includeFrontCover"
                label={t("surveyViews.labels.includeFrontCover")}
                control={form.control}
              />

              <HookFormCheckbox
                name="config.includeTableOfContent"
                label={t("surveyViews.labels.includeTableOfContent")}
                control={form.control}
              />

              <BaseTooltip
                title={
                  isOrganizationOverviewBlank
                    ? t("surveyViews.labels.includeOrganizationSummaryTooltip")
                    : ""
                }
              >
                <HookFormCheckbox
                  disabled={isOrganizationOverviewBlank}
                  name="config.includeOrganizationSummary"
                  label={t("surveyViews.labels.includeOrganizationSummary")}
                  control={form.control}
                />
              </BaseTooltip>

              <HookFormCheckbox
                name="config.includeContributors"
                label={t("surveyViews.labels.includeContributors")}
                disabled={!formData.config.includeOrganizationSummary}
                control={form.control}
              />

              {formData.config.includeContributors && (
                <div className="survey-view-customization-form-panel-general-fields-contributors">
                  {isSurveyContributorsLoading && <BaseSpinner size="small" />}
                  {surveyContributors && (
                    <HookFormBaseDropdown
                      name="config.contributors"
                      control={form.control}
                      options={surveyContributors.map((contributor) => ({
                        label: contributor.fullName,
                        value: contributor.id,
                      }))}
                      multiple
                      placeholder={t(
                        "surveyViews.placeholders.selectContributors",
                      )}
                    />
                  )}
                </div>
              )}

              <HookFormCheckbox
                name="config.includePrmePage"
                label={t("surveyViews.labels.includePrmePage")}
                control={form.control}
              />

              <HookFormCheckbox
                name="config.includeSdgPage"
                label={t("surveyViews.labels.includeSdgPage")}
                control={form.control}
              />

              <HookFormCheckbox
                name="config.includeBackCover"
                label={t("surveyViews.labels.includeBackCover")}
                control={form.control}
              />

              <HookFormCheckbox
                name="config.includeDividers"
                label={t("surveyViews.labels.includeDividerPages")}
                control={form.control}
              />

              <HookFormCheckbox
                name="config.grayScale"
                label={t("surveyViews.labels.grayscale")}
                control={form.control}
              />
            </div>
          </div>

          <div className="survey-view-customization-form-panel-design">
            <h3 className="body-m-medium">
              {t("surveyViews.labels.frontPageDesign")}
            </h3>

            <p className="body-a-regular">
              {t("surveyViews.labels.designDescription")}
            </p>

            <FormFieldWrapper label={t("surveyViews.labels.backgroundColor")}>
              <HookFormColorPicker
                name="config.coverPageBackgroundColor"
                control={form.control}
                showText
                defaultValue="#1E3250"
              />
            </FormFieldWrapper>
          </div>
        </div>

        <div className="survey-view-customization-form-preview">
          <Preview surveyViewData={formData} />
        </div>
      </div>
    </div>
  );
};

export default SurveyViewFormCustomization;
